import { EMOTIONS, EMOTION_PHASES } from "@/lib/emotions";

/**
 * A three-phase emotion chip picker (Entry / Management / Exit).
 * Value is the current form object; onChange fires with the updated form.
 * Chip clicks toggle — clicking the selected chip clears the phase.
 */
export function EmotionPicker({ value, onChange, testIdPrefix = "journal-emotion" }) {
  const setPhase = (field, key) => {
    onChange({ ...value, [field]: value[field] === key ? null : key });
  };

  return (
    <div
      data-testid={`${testIdPrefix}-picker`}
      className="col-span-2 sm:col-span-4 rounded-lg bg-white border border-slate-200 p-3"
    >
      <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
        How did each phase feel?
      </div>
      <p className="mt-0.5 text-[11px] text-slate-400 leading-relaxed">
        Optional — tag your emotional read so the weekly report can spot patterns.
      </p>
      <div className="mt-3 space-y-3">
        {EMOTION_PHASES.map((phase) => {
          const selectedKey = value[phase.field] || null;
          return (
            <div key={phase.field} className="grid grid-cols-1 sm:grid-cols-[110px_1fr] gap-2 sm:items-center">
              <div>
                <div className="text-[11px] font-semibold text-slate-700">{phase.label}</div>
                <div className="text-[10px] text-slate-400 leading-tight">{phase.hint}</div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {EMOTIONS.map((emo) => {
                  const selected = selectedKey === emo.key;
                  return (
                    <button
                      key={emo.key}
                      type="button"
                      onClick={() => setPhase(phase.field, emo.key)}
                      data-testid={`${testIdPrefix}-${phase.field}-${emo.key}`}
                      aria-pressed={selected}
                      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border transition-all ${
                        selected
                          ? "bg-blue-800 border-blue-800 text-white shadow-sm"
                          : "bg-slate-50 border-slate-200 text-slate-700 hover:border-blue-800 hover:text-blue-800"
                      }`}
                    >
                      <span aria-hidden className="text-sm leading-none">{emo.emoji}</span>
                      <span>{emo.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Compact three-emoji badge for the trade-list row.
 * Renders nothing if the trade has no emotions tagged.
 */
export function EmotionRowBadges({ entry }) {
  const shown = EMOTION_PHASES
    .map((p) => ({ ...p, key: entry?.[p.field] }))
    .filter((p) => !!p.key);
  if (shown.length === 0) return null;

  return (
    <div
      data-testid="journal-emotion-badges"
      className="inline-flex items-center gap-1"
      aria-label="Trade emotions"
    >
      {shown.map((p) => {
        const emo = EMOTIONS.find((e) => e.key === p.key);
        if (!emo) return null;
        return (
          <span
            key={p.field}
            title={`${p.label}: ${emo.label}`}
            className="text-base leading-none"
          >
            {emo.emoji}
          </span>
        );
      })}
    </div>
  );
}
